import type { ComponentChildren } from 'preact'
import type { WebsterChatMessage, WebsterAttachment } from '@/types/webster'
import { WebsterChatArea } from '@/components/webster/WebsterChatArea'
import { WebsterChatInput } from '@/components/webster/WebsterChatInput'

/**
 * Minimum surface of the chat hook return that WebsterChatPanel needs to render.
 * Both useLandingPageChat and useEmailAssetChat return objects assignable to
 * this — any future asset-type chat hook just needs to expose the same two
 * fields and it slots in for free.
 */
export interface WebsterChatState {
    messages: WebsterChatMessage[]
    generating: boolean
}

interface WebsterChatPanelProps {
    /**
     * The chat hook return. Drives the message list + streaming indicator.
     * Per-message model+token attribution comes from the messages themselves
     * (rendered by MessageTokenFooter inside the chat area).
     */
    chat: WebsterChatState

    /** Send callback for the input. Fires on Enter or button click. */
    onSend: (message: string, attachments?: WebsterAttachment[]) => void

    /** Disables the input for this user's own in-flight turn. */
    disabled?: boolean

    /**
     * When provided, the composer shows an image-attach affordance (paperclip).
     * Picks are validated + uploaded via this callback. Omit it to keep the
     * text-only composer (e.g. the pre-generation setup input).
     */
    onUploadImage?: (file: File) => Promise<{ url: string; fileName?: string; contentType?: string }>


    /**
     * When another user holds the asset-chat turn, attribute the wait in the
     * input placeholder ("Joel is asking Webster…") and disable the textarea.
     * Distinct from `disabled` which represents this user's own in-flight turn.
     */
    lockedByUserName?: string | null

    /** Content shown when the message list is empty AND nothing is generating. */
    emptyMessage?: ComponentChildren

    /** Input placeholder text. */
    placeholder?: string

    /** Override the default "Send" button label (e.g. "Generate" on initial gen). */
    buttonLabel?: string

    /** LP-specific — show the inline "Generate Thank You Page" button. */
    suggestThankYouGeneration?: boolean
    onGenerateThankYou?: () => void
}

/**
 * Self-contained chat surface. Wraps the WebsterChatArea (message list +
 * streaming indicator) and the WebsterChatInput (textarea + send button),
 * and threads the live token counter through internally so parents pass
 * `chat` once instead of drilling streamedChars / confirmedTokens through
 * multiple layers of unrelated components.
 *
 * Both the LP builder editor panel and the email builder use this directly —
 * they only forward the `chat` object plus a few UI-specific prop overrides.
 */
export function WebsterChatPanel({
    chat,
    onSend,
    onUploadImage,
    disabled,
    lockedByUserName,
    emptyMessage,
    placeholder,
    buttonLabel,
    suggestThankYouGeneration,
    onGenerateThankYou,
}: WebsterChatPanelProps) {
    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <WebsterChatArea
                messages={chat.messages}
                generating={chat.generating}
                emptyMessage={emptyMessage}
                suggestThankYouGeneration={suggestThankYouGeneration}
                onGenerateThankYou={onGenerateThankYou}
            />
            <WebsterChatInput
                onSend={onSend}
                onUploadImage={onUploadImage}
                disabled={disabled}
                lockedByUserName={lockedByUserName}
                placeholder={placeholder}
                buttonLabel={buttonLabel}
            />
        </div>
    )
}
